// Pure currency-conversion math, deliberately kept free of any db/account
// access so it's trivially unit-testable and usable from both client and
// server code. Callers are responsible for resolving which currencies and
// rate apply -- this module only does the arithmetic.

import type { ExchangeRateEntity } from '#types/models';

/**
 * Converts an integer amount (in the smallest unit of its *source*
 * currency, e.g. cents) to the smallest unit of a *target* currency, using
 * the given major-unit exchange rate (e.g. a USD -> JPY rate of 150 means
 * 1 USD = 150 JPY, not 1 cent = 150 sen).
 *
 * Different currencies use different numbers of decimal places for their
 * smallest unit -- most use 2 (cents), but e.g. JPY/KRW/IRR use 0 (their
 * smallest unit is a whole unit, no subdivision). Naively multiplying the
 * stored integer amounts together without accounting for this would be off
 * by a power of 10 whenever source and target differ (e.g. a stored USD
 * amount of 10000 -- $100.00 -- converted at a 150 JPY rate would wrongly
 * produce 1,500,000 instead of the correct 15,000 yen).
 *
 * Returns null (not the original amount) when no rate is available, or when
 * the rate is not a valid exchange rate -- zero, negative, or non-finite
 * (Infinity/-Infinity/NaN). A real currency exchange rate is always a
 * finite positive number; a stored 0 would silently zero out every
 * converted amount, a negative rate would silently flip every amount's
 * sign, and Infinity would silently produce an infinite converted amount.
 * Callers must decide how to represent "cannot convert this" (e.g. show
 * the native amount with an indicator that it's unconverted), rather than
 * this function silently corrupting the result.
 */
export function convertAmount(
  amount: number,
  rate: number | null,
  fromDecimalPlaces: number,
  toDecimalPlaces: number,
): number | null {
  if (rate == null || !Number.isFinite(rate) || rate <= 0) return null;

  const scale = Math.pow(10, toDecimalPlaces - fromDecimalPlaces);
  const result = amount * rate * scale;

  // Math.round() rounds half-way values toward +Infinity (e.g. rounds -0.5
  // to -0, but 0.5 to 1), which is asymmetric: an outflow and an equal-
  // magnitude inflow landing on a half-unit boundary would convert to
  // different magnitudes, so they wouldn't net to zero (e.g. an outflow
  // followed by an exact refund). Rounding the absolute value and
  // reapplying the sign keeps outflow/inflow conversions symmetric.
  const roundedMagnitude = Math.round(Math.abs(result));

  // A tiny negative amount (e.g. a small outflow converted into a
  // zero-decimal-place currency) can round down to a magnitude of 0, and
  // Math.sign(result) * 0 produces JavaScript's -0. -0 === 0 numerically,
  // but formatters can render it as "-0"/"-$0.00", showing a phantom
  // negative amount. Return plain 0 explicitly in that case.
  if (roundedMagnitude === 0) return 0;

  return Math.sign(result) * roundedMagnitude;
}

/**
 * Finds the most recent stored rate on or before a date for an *exact*
 * (fromCurrency, toCurrency) direction -- no inversion -- along with the
 * date it was recorded on, so callers can compare recency against a
 * candidate from the opposite direction. Pure and db-independent, usable
 * from both client (given an already-fetched rates array) and server code.
 *
 * Filters out non-positive/non-finite rates even though the exchange-rates
 * server handlers already reject them at write time: CRDT sync's apply()
 * writes column values directly via INSERT/UPDATE, bypassing those
 * handlers entirely (confirmed in server/sync/index.ts), so a bad rate
 * written by another client (different version, corrupted state, etc.) can
 * still land in the table. Write-path validation alone doesn't cover
 * everything a distributed sync system can produce, so the read path
 * defends itself too.
 */
export function findDirectRate(
  rates: ExchangeRateEntity[],
  fromCurrency: string,
  toCurrency: string,
  date: string,
): { rate: number; date: string } | null {
  const candidates = rates
    .filter(
      r =>
        r.from_currency === fromCurrency &&
        r.to_currency === toCurrency &&
        r.date <= date &&
        Number.isFinite(r.rate) &&
        r.rate > 0,
    )
    .sort((a, b) => (a.date < b.date ? 1 : -1));

  return candidates.length > 0
    ? { rate: candidates[0].rate, date: candidates[0].date }
    : null;
}

/**
 * Resolves the rate in effect for a given date and currency pair, checking
 * both the exact stored direction and the inverse of the opposite
 * direction's rate (1 / rate), and using whichever candidate was recorded
 * more recently. This is what lets switching a budget file's base currency
 * actually work with whatever data you already have: if you've been
 * fetching DKK -> EUR rates and then switch the budget's base currency to
 * DKK, your (newly-foreign) EUR accounts need EUR -> DKK, which this
 * derives from the DKK -> EUR data you already have rather than requiring
 * you to separately fetch/store the reverse direction too.
 *
 * Recency, not direction, decides which candidate wins: a stale manually-
 * entered direct rate from months ago must not shadow a much fresher
 * inverse derived from ongoing fetches of the opposite direction (or vice
 * versa) -- "rate in effect for this date" means whichever data source last
 * had something to say about it, regardless of which direction it was
 * recorded in.
 */
export function resolveRate(
  rates: ExchangeRateEntity[],
  fromCurrency: string,
  toCurrency: string,
  date: string,
): number | null {
  const direct = findDirectRate(rates, fromCurrency, toCurrency, date);
  const inverse = findDirectRate(rates, toCurrency, fromCurrency, date);

  if (direct != null && (inverse == null || direct.date >= inverse.date)) {
    return direct.rate;
  }
  if (inverse != null) {
    return 1 / inverse.rate;
  }
  return null;
}
